import type { FollowupsByOwner, Links } from '@ops/contracts'

import { formatFechaCorta, iniciales, plural } from '../../lib/format'
import { Panel, RecordChip, Stack } from '../../ui'
import { contarFollowups } from './frentes'

/**
 * Riel de seguimientos, agrupados por dueño. Es la columna derecha de la Zona 1.
 *
 * El panel **no gestiona el estado**: cada chip abre el work item en Plane, que
 * es la fuente de verdad. El estado se muestra tal como lo escribieron allá
 * (`estado`), y el tono del chip sale de la agrupación normalizada
 * (`estadoGroup`), así el panel no tiene que conocer los estados del workspace.
 *
 * Cuando falta el `planeProjectId` el deep link llega `null` desde el servidor y
 * el chip muestra "sin vincular" en vez de un link a una 404.
 */
export interface FollowupsRailProps {
  grupos: FollowupsByOwner
  links: Links
  /** "Seguimientos abiertos" para el founder, "Mis seguimientos" para el resto. */
  titulo: string
  /** Qué decir cuando no hay ninguno. */
  vacio: string
}

export function FollowupsRail({
  grupos,
  links,
  titulo,
  vacio,
}: FollowupsRailProps) {
  const total = contarFollowups(grupos)
  const conAlgo = grupos.filter((g) => g.followups.length > 0)

  return (
    <Panel
      titulo={titulo}
      subtitulo={
        total === 0
          ? undefined
          : `${total} ${plural(total, 'abierto', 'abiertos')} en ${conAlgo.length} ${plural(conAlgo.length, 'dueño', 'dueños')}`
      }
      padding="sm"
      footer="El estado se lee de Plane. Para cambiarlo, abrí el work item."
    >
      {conAlgo.length === 0 ? (
        <p
          data-testid="followups-vacio"
          className="text-meta text-muted-strong"
        >
          {vacio}
        </p>
      ) : (
        <Stack gap="md">
          {conAlgo.map((grupo) => (
            <div
              key={grupo.owner?.id ?? 'sin-dueno'}
              data-testid="followups-grupo"
              className="min-w-0"
            >
              <p className="mb-1.5 inline-flex items-center gap-1.5 text-meta font-medium text-text">
                <span
                  aria-hidden="true"
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-n100 text-[9px] font-bold"
                >
                  {iniciales(grupo.owner?.nombre)}
                </span>
                {grupo.owner?.nombre ?? 'Sin dueño'}
                <span className="tabular text-micro text-muted-strong">
                  ({grupo.followups.length})
                </span>
              </p>

              <Stack as="ul" gap="sm">
                {grupo.followups.map((followup) => {
                  const fecha = formatFechaCorta(followup.fechaObjetivo)
                  return (
                    <li key={followup.id} className="min-w-0">
                      <p className="text-meta text-text">{followup.titulo}</p>
                      <span className="mt-1 flex flex-wrap items-center gap-2">
                        <RecordChip
                          link={links.followups[followup.id]?.plane ?? null}
                          identificador={followup.planeIdentificador}
                          estado={followup.estado}
                          estadoGroup={followup.estadoGroup}
                        />
                        {fecha !== null ? (
                          <span className="tabular text-micro text-muted-strong">
                            objetivo {fecha}
                          </span>
                        ) : (
                          <span className="text-micro text-muted-strong">
                            sin fecha objetivo
                          </span>
                        )}
                      </span>
                    </li>
                  )
                })}
              </Stack>
            </div>
          ))}
        </Stack>
      )}
    </Panel>
  )
}
